import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as designItemService from '../services/designItemService';

export const designItemKeys = {
  all: (projectId: string) => ['designItems', projectId] as const,
  list: (projectId: string, params?: Record<string, string | number | undefined>) =>
    ['designItems', projectId, 'list', params] as const,
  detail: (projectId: string, designItemId: string) =>
    ['designItems', projectId, designItemId] as const,
  byIssue: (projectId: string, issueId: string) =>
    ['designItems', 'byIssue', projectId, issueId] as const,
};

export function useDesignItems(
  projectId: string,
  params?: Record<string, string | number | undefined>,
) {
  return useQuery({
    queryKey: designItemKeys.list(projectId, params),
    queryFn: () => designItemService.fetchDesignItems(projectId, params),
    enabled: !!projectId,
  });
}

export function useDesignItemDetail(projectId: string, designItemId: string) {
  return useQuery({
    queryKey: designItemKeys.detail(projectId, designItemId),
    queryFn: () => designItemService.fetchDesignItemDetail(projectId, designItemId),
    enabled: !!projectId && !!designItemId,
  });
}

export function useCreateDesignItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      projectId: string;
      data: FormData | designItemService.CreateDesignItemInput;
    }) => designItemService.createDesignItem(params.projectId, params.data),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: designItemKeys.all(params.projectId) });
    },
  });
}

export function useUpdateDesignItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      projectId: string;
      designItemId: string;
      data: Record<string, unknown>;
    }) => designItemService.updateDesignItem(params.projectId, params.designItemId, params.data),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: designItemKeys.all(params.projectId) });
      queryClient.invalidateQueries({
        queryKey: designItemKeys.detail(params.projectId, params.designItemId),
      });
    },
  });
}

export function useDeleteDesignItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; designItemId: string }) =>
      designItemService.deleteDesignItem(params.projectId, params.designItemId),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: designItemKeys.all(params.projectId) });
    },
  });
}

export function useChangeDesignItemStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      projectId: string;
      designItemId: string;
      status: string;
      comment?: string;
    }) =>
      designItemService.changeDesignItemStatus(
        params.projectId,
        params.designItemId,
        params.status,
        params.comment,
      ),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: designItemKeys.all(params.projectId) });
      queryClient.invalidateQueries({
        queryKey: designItemKeys.detail(params.projectId, params.designItemId),
      });
    },
  });
}

export function useRefreshThumbnail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; designItemId: string }) =>
      designItemService.refreshThumbnail(params.projectId, params.designItemId),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: designItemKeys.detail(params.projectId, params.designItemId),
      });
      queryClient.invalidateQueries({ queryKey: designItemKeys.all(params.projectId) });
    },
  });
}

export function useAddDesignLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      projectId: string;
      designItemId: string;
      issueId: string;
      linkType: string;
    }) =>
      designItemService.addDesignLink(
        params.projectId,
        params.designItemId,
        params.issueId,
        params.linkType,
      ),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: designItemKeys.detail(params.projectId, params.designItemId),
      });
    },
  });
}

export function useRemoveDesignLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; designItemId: string; linkId: string }) =>
      designItemService.removeDesignLink(params.projectId, params.designItemId, params.linkId),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: designItemKeys.detail(params.projectId, params.designItemId),
      });
    },
  });
}

export function useIssueDesignItems(projectId: string, issueId: string) {
  return useQuery({
    queryKey: designItemKeys.byIssue(projectId, issueId),
    queryFn: () => designItemService.fetchIssueDesignItems(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}
